import Tooltip from 'components/layout/tooltip';

type TileProps = {
  label: string;
  // Second line of the two-line want-tile label; single-line tiles omit it.
  line2?: string;
  selected: boolean;
  // Unreachable option (want component picker): rendered but not clickable.
  // A selected tile is never disabled — deselection is always allowed.
  disabled?: boolean;
  tooltip?: string;
  // Attribution dot class (want tiles) — rendered before the label.
  dotClass?: string;
  onClick?: () => void;
};

// One selectable tile in a wrap canvas (AHA-6681 §B/§G). Always a plain
// button so the canvases stay keyboard-accessible.
export default function Tile({
  label,
  line2,
  selected,
  disabled = false,
  tooltip,
  dotClass,
  onClick
}: TileProps) {
  let stateClass =
    'cursor-pointer border-gray-300 bg-white text-gray-700 hover:border-gray-400';
  if (selected) {
    stateClass =
      'cursor-pointer border-blue-600 bg-blue-50 font-semibold text-blue-800';
  } else if (disabled) {
    stateClass = 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400';
  }
  const tooltipNode = tooltip ? <Tooltip text={tooltip} /> : null;
  return (
    <span
      className={`relative inline-flex${disabled ? ' cursor-not-allowed' : ''}`}
    >
      <button
        type='button'
        aria-pressed={selected}
        disabled={disabled}
        onClick={onClick}
        className={`relative inline-flex flex-col items-start gap-0.5 rounded-lg border px-3 py-1.5 text-left text-xs leading-snug ${stateClass}`}
      >
        <span className='inline-flex items-center gap-1.5'>
          {dotClass && (
            <span
              data-testid='want-tile-dot'
              className={`inline-block h-2 w-2 rounded-full ${dotClass}`}
            />
          )}
          {label}
        </span>
        {line2 && (
          <span
            className={`text-[10.5px] font-normal ${
              selected ? 'text-blue-500' : 'text-gray-500'
            }`}
          >
            {line2}
          </span>
        )}
        {!disabled && tooltipNode}
      </button>
      {disabled && tooltipNode && (
        <span className='absolute inset-0 z-10 cursor-not-allowed'>
          {tooltipNode}
        </span>
      )}
    </span>
  );
}
